import { useState, useEffect } from 'react';
import { performanceMetricsService } from '../../../services/performanceMetricsService';

export const usePerformanceMetrics = () => {
  const [realTimeData, setRealTimeData] = useState({
    apiResponseTime: 142,
    dbQueryTime: 23,
    sessionQuality: 94.2,
    activeConnections: 47
  });
  const [alerts, setAlerts] = useState([]);
  const [serviceHealth, setServiceHealth] = useState([]);
  const [chartData, setChartData] = useState({
    apiPerformance: [],
    dbPerformance: [],
    sessionQuality: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let channel = null;

    const initializeMetrics = async () => {
      try {
        // Fetch latest metrics
        const latestMetrics = await performanceMetricsService?.getLatestMetrics();
        setRealTimeData(latestMetrics);

        // Fetch alerts
        const alertsData = await performanceMetricsService?.detectAlerts();
        setAlerts(alertsData);

        // Fetch service health
        const healthData = await performanceMetricsService?.getServiceHealth();
        setServiceHealth(healthData);

        // Fetch chart data for last 24 hours
        const [apiChart, dbChart, sessionChart] = await Promise.all([
          performanceMetricsService?.getChartData('api_response', 24),
          performanceMetricsService?.getChartData('db_query', 24),
          performanceMetricsService?.getChartData('session_quality', 24)
        ]);

        setChartData({
          apiPerformance: apiChart,
          dbPerformance: dbChart,
          sessionQuality: sessionChart
        });

        setLoading(false);

        // Subscribe to real-time updates
        channel = performanceMetricsService?.subscribeToMetrics(async (newMetric) => {
          setRealTimeData(prev => {
            const updated = { ...prev };
            
            switch (newMetric?.metric_type) {
              case 'api_response':
                updated.apiResponseTime = newMetric?.metric_value;
                break;
              case 'db_query':
                updated.dbQueryTime = newMetric?.metric_value;
                break;
              case 'session_quality':
                updated.sessionQuality = newMetric?.metric_value;
                break;
              case 'active_connections':
                updated.activeConnections = newMetric?.metric_value;
                break;
              default:
                break;
            }
            
            return updated;
          });

          // Refresh alerts and service health when new metrics arrive
          const newAlerts = await performanceMetricsService?.detectAlerts();
          setAlerts(newAlerts);

          const newHealth = await performanceMetricsService?.getServiceHealth();
          setServiceHealth(newHealth);
        });
      } catch (err) {
        console.error('Error initializing metrics:', err);
        setError(err?.message || 'Failed to load metrics');
        setLoading(false);
      }
    };

    initializeMetrics();

    return () => {
      if (channel) {
        performanceMetricsService?.unsubscribe(channel);
      }
    };
  }, []);

  return { realTimeData, alerts, serviceHealth, chartData, loading, error };
};